/**
 * Panel para gestionar las inscripciones de un evento
 */

import { useState, useMemo, useCallback } from "react";
import { Button, Badge } from "@/components/ui";
import { toast } from "react-hot-toast";
import {
  X,
  Loader2,
  Search,
  Check,
  XCircle,
  Clock,
  Users,
  DollarSign,
  Download,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  CheckCheck,
  Filter,
} from "lucide-react";
import {
  useEventRegistrations,
  useUpdateRegistrationStatus,
  useEventStats,
} from "@/hooks/useEvents";
import type { Event, EventRegistration, RegistrationStatus } from "@/types";
import { exportToXlsx } from "@/lib/utils/exportXlsx";
import { buildPersonsMap, personColumns } from "@/lib/utils/eventExport";
import { usePersons } from "@/hooks/usePersons";
import { Can } from "@/components/auth/Can";

// Helper para convertir timestamp a Date de forma segura
const toSafeDate = (timestamp: unknown): Date => {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  if (
    typeof timestamp === "object" &&
    "toDate" in timestamp &&
    typeof (timestamp as { toDate: () => Date }).toDate === "function"
  ) {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  if (typeof timestamp === "object" && "seconds" in timestamp) {
    return new Date((timestamp as { seconds: number }).seconds * 1000);
  }
  if (typeof timestamp === "string" || typeof timestamp === "number") {
    return new Date(timestamp);
  }
  return new Date();
};

interface EventRegistrationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  event: Event;
  canManage?: boolean;
  filterByClubId?: string;
}

const statusLabels: Record<RegistrationStatus, string> = {
  pending: "Pendiente",
  approved: "Aprobado",
  rejected: "Rechazado",
  cancelled: "Cancelado",
  waitlist: "Lista de espera",
};

const statusColors: Record<RegistrationStatus, string> = {
  pending: "bg-yellow-100 text-yellow-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  cancelled: "bg-gray-100 text-gray-700",
  waitlist: "bg-blue-100 text-blue-700",
};

const categoryLabels: Record<string, string> = {
  pathfinder: "Conquistador",
  staff: "Staff",
  guest: "Invitado",
  family: "Familiar",
};

export default function EventRegistrationsPanel({
  isOpen,
  onClose,
  event,
  canManage = false,
  filterByClubId,
}: EventRegistrationsPanelProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<RegistrationStatus | "all">(
    "all",
  );
  const [selectedRegistrations, setSelectedRegistrations] = useState<
    Set<string>
  >(new Set());
  const [expandedClubs, setExpandedClubs] = useState<Set<string>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [showOnlyPending, setShowOnlyPending] = useState(false);

  const { data: registrations, isLoading } = useEventRegistrations(event.id);
  const { data: stats } = useEventStats(event.id);
  const { data: persons } = usePersons();
  const updateStatus = useUpdateRegistrationStatus();

  // Filtrar inscripciones
  const filteredRegistrations = useMemo(() => {
    if (!registrations) return [];

    return registrations.filter((reg) => {
      if (filterByClubId && reg.clubId !== filterByClubId) return false;
      if (showOnlyPending && reg.status !== "pending") return false;
      if (statusFilter === "all") {
        if (reg.status === "cancelled" || reg.status === "rejected")
          return false;
      } else if (reg.status !== statusFilter) {
        return false;
      }

      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        return (
          reg.personName.toLowerCase().includes(search) ||
          reg.clubName.toLowerCase().includes(search)
        );
      }

      return true;
    });
  }, [
    registrations,
    statusFilter,
    searchTerm,
    filterByClubId,
    showOnlyPending,
  ]);

  // Agrupar por club con resumen
  const clubGroups = useMemo(() => {
    const groups: Record<
      string,
      {
        clubName: string;
        registrations: EventRegistration[];
        pending: number;
        approved: number;
        rejected: number;
        total: number;
        totalAmount: number;
      }
    > = {};

    filteredRegistrations.forEach((reg) => {
      if (!groups[reg.clubId]) {
        groups[reg.clubId] = {
          clubName: reg.clubName,
          registrations: [],
          pending: 0,
          approved: 0,
          rejected: 0,
          total: 0,
          totalAmount: 0,
        };
      }
      const g = groups[reg.clubId];
      g.registrations.push(reg);
      g.total++;
      g.totalAmount += reg.unitPrice;
      if (reg.status === "pending") g.pending++;
      else if (reg.status === "approved") g.approved++;
      else if (reg.status === "rejected") g.rejected++;
    });

    // Ordenar: clubes con pendientes primero, luego por nombre
    return Object.entries(groups).sort((a, b) => {
      if (a[1].pending > 0 && b[1].pending === 0) return -1;
      if (a[1].pending === 0 && b[1].pending > 0) return 1;
      return a[1].clubName.localeCompare(b[1].clubName);
    });
  }, [filteredRegistrations]);

  // Total de pendientes visibles
  const pendingCount =
    registrations?.filter((r) => r.status === "pending").length || 0;
  const filteredPendingCount = filteredRegistrations.filter(
    (r) => r.status === "pending",
  ).length;

  const toggleClub = useCallback((clubId: string) => {
    setExpandedClubs((prev) => {
      const next = new Set(prev);
      if (next.has(clubId)) next.delete(clubId);
      else next.add(clubId);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedClubs(new Set(clubGroups.map(([id]) => id)));
  }, [clubGroups]);

  const collapseAll = useCallback(() => {
    setExpandedClubs(new Set());
  }, []);

  // Selección por club
  const toggleClubSelection = useCallback(
    (clubId: string) => {
      const group = clubGroups.find(([id]) => id === clubId);
      if (!group) return;
      const clubRegIds = group[1].registrations
        .filter((r) => r.status === "pending")
        .map((r) => r.id);

      setSelectedRegistrations((prev) => {
        const next = new Set(prev);
        const allSelected = clubRegIds.every((id) => next.has(id));
        if (allSelected) {
          clubRegIds.forEach((id) => next.delete(id));
        } else {
          clubRegIds.forEach((id) => next.add(id));
        }
        return next;
      });
    },
    [clubGroups],
  );

  const toggleSelection = (id: string) => {
    setSelectedRegistrations((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllPending = useCallback(() => {
    const pendingIds = filteredRegistrations
      .filter((r) => r.status === "pending")
      .map((r) => r.id);
    setSelectedRegistrations(new Set(pendingIds));
  }, [filteredRegistrations]);

  const clearSelection = useCallback(() => {
    setSelectedRegistrations(new Set());
  }, []);

  // Bulk actions con feedback
  const handleBulkAction = useCallback(
    async (status: "approved" | "rejected") => {
      if (selectedRegistrations.size === 0) return;
      const label = status === "approved" ? "aprobar" : "rechazar";

      setIsBulkProcessing(true);
      let succeeded = 0;
      let failed = 0;

      for (const id of selectedRegistrations) {
        try {
          await updateStatus.mutateAsync({
            id,
            status,
            eventId: event.id,
          });
          succeeded++;
        } catch {
          failed++;
        }
      }

      setSelectedRegistrations(new Set());
      setIsBulkProcessing(false);

      if (failed === 0) {
        toast.success(
          `${succeeded} inscripción(es) ${status === "approved" ? "aprobada" : "rechazada"}(s)`,
        );
      } else {
        toast.error(`${succeeded} ok, ${failed} con error al ${label}`);
      }
    },
    [selectedRegistrations, updateStatus, event.id],
  );

  // Aprobar todo un club
  const handleApproveClub = useCallback(
    async (clubId: string) => {
      const group = clubGroups.find(([id]) => id === clubId);
      if (!group) return;
      const pendingRegs = group[1].registrations.filter(
        (r) => r.status === "pending",
      );
      if (pendingRegs.length === 0) return;

      setIsBulkProcessing(true);
      let succeeded = 0;
      let failed = 0;

      for (const reg of pendingRegs) {
        try {
          await updateStatus.mutateAsync({
            id: reg.id,
            status: "approved",
            eventId: event.id,
          });
          succeeded++;
        } catch {
          failed++;
        }
      }

      setIsBulkProcessing(false);
      if (failed === 0) {
        toast.success(
          `${group[1].clubName}: ${succeeded} inscripción(es) aprobada(s)`,
        );
      } else {
        toast.error(
          `${group[1].clubName}: ${succeeded} ok, ${failed} con error`,
        );
      }
    },
    [clubGroups, updateStatus, event.id],
  );

  // Aprobar TODOS los pendientes
  const handleApproveAllPending = useCallback(async () => {
    if (!registrations) return;
    const pending = registrations.filter((r) => r.status === "pending");
    if (pending.length === 0) return;

    setIsBulkProcessing(true);
    let succeeded = 0;
    let failed = 0;

    for (const reg of pending) {
      try {
        await updateStatus.mutateAsync({
          id: reg.id,
          status: "approved",
          eventId: event.id,
        });
        succeeded++;
      } catch {
        failed++;
      }
    }

    setIsBulkProcessing(false);
    setSelectedRegistrations(new Set());

    if (failed === 0) {
      toast.success(`¡${succeeded} inscripción(es) aprobada(s)!`);
    } else {
      toast.error(`${succeeded} ok, ${failed} con error`);
    }
  }, [registrations, updateStatus, event.id]);

  const handleApprove = async (registration: EventRegistration) => {
    await updateStatus.mutateAsync({
      id: registration.id,
      status: "approved",
      eventId: event.id,
    });
  };

  const handleReject = async (registration: EventRegistration) => {
    await updateStatus.mutateAsync({
      id: registration.id,
      status: "rejected",
      eventId: event.id,
    });
  };

  const exportToCSV = () => {
    if (!registrations) return;
    const personsMap = buildPersonsMap(persons);
    exportToXlsx({
      filename: `inscripciones-${event.name.replace(/\s+/g, "-")}`,
      sheetName: "Inscripciones",
      rows: filteredRegistrations,
      columns: [
        { header: "Nombre", key: (r) => r.personName, width: 35 },
        { header: "Club", key: (r) => r.clubName, width: 25 },
        ...personColumns(personsMap),
        {
          header: "Categoría",
          key: (r) => categoryLabels[r.category] || r.category,
          width: 18,
        },
        {
          header: "Estado",
          key: (r) => statusLabels[r.status],
          width: 14,
        },
        { header: "Estado Pago", key: (r) => r.paymentStatus, width: 14 },
        { header: "Monto", key: (r) => r.unitPrice, width: 12 },
        {
          header: "Fecha Inscripción",
          key: (r) =>
            toSafeDate(r.registrationDate).toLocaleDateString("es-CO"),
          width: 18,
        },
      ],
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-purple-100 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-t-2xl">
          <div className="text-white">
            <h2 className="text-xl font-semibold">Inscripciones</h2>
            <p className="text-purple-100 text-sm mt-0.5">
              {event.name} — {clubGroups.length} club
              {clubGroups.length !== 1 ? "es" : ""},{" "}
              {filteredRegistrations.length} inscripciones
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={exportToCSV}>
              <Download className="w-4 h-4 mr-1" />
              Excel
            </Button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors duration-200"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Estadísticas compactas */}
        <div className="flex items-center gap-6 px-5 py-3 bg-gray-50/80 border-b border-gray-100 text-sm">
          <span className="flex items-center gap-1.5">
            <Users className="w-4 h-4 text-purple-500" />
            <strong>{stats?.total || 0}</strong> total
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-amber-500" />
            <strong className="text-amber-700">
              {stats?.pending || 0}
            </strong>{" "}
            pendientes
          </span>
          <span className="flex items-center gap-1.5">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <strong className="text-green-700">
              {stats?.approved || 0}
            </strong>{" "}
            aprobados
          </span>
          <span className="flex items-center gap-1.5">
            <XCircle className="w-4 h-4 text-red-500" />
            <strong>{stats?.rejected || 0}</strong> rechazados
          </span>
          <span className="flex items-center gap-1.5 ml-auto">
            <DollarSign className="w-4 h-4 text-emerald-500" />
            <strong>
              ${(stats?.totalPaid || 0).toLocaleString("es-CO")}
            </strong>{" "}
            recaudado
          </span>
        </div>

        {/* Barra de acciones prominente para pendientes */}
        {canManage && pendingCount > 0 && (
          <div className="flex items-center gap-3 px-5 py-3 bg-amber-50 border-b border-amber-200">
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
            <span className="text-sm font-medium text-amber-800">
              {pendingCount} pendiente{pendingCount !== 1 ? "s" : ""} por
              aprobar
            </span>
            <div className="flex items-center gap-2 ml-auto">
              {selectedRegistrations.size > 0 && (
                <>
                  <span className="text-xs text-gray-500">
                    {selectedRegistrations.size} seleccionado
                    {selectedRegistrations.size !== 1 ? "s" : ""}
                  </span>
                  <Can permission="events.approve">
                    <Button
                      size="sm"
                      onClick={() => handleBulkAction("approved")}
                      disabled={isBulkProcessing}
                    >
                      {isBulkProcessing ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <Check className="w-3 h-3 mr-1" />
                      )}
                      Aprobar selección
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleBulkAction("rejected")}
                      disabled={isBulkProcessing}
                    >
                      <XCircle className="w-3 h-3 mr-1" />
                      Rechazar
                    </Button>
                  </Can>
                  <button
                    onClick={clearSelection}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Limpiar
                  </button>
                  <div className="w-px h-6 bg-amber-300" />
                </>
              )}
              <Button
                size="sm"
                variant="secondary"
                onClick={selectAllPending}
                disabled={isBulkProcessing}
              >
                <CheckCheck className="w-3 h-3 mr-1" />
                Seleccionar pendientes ({filteredPendingCount})
              </Button>
              <Can permission="events.approve">
                <Button
                  size="sm"
                  onClick={handleApproveAllPending}
                  disabled={isBulkProcessing}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {isBulkProcessing ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <CheckCheck className="w-3 h-3 mr-1" />
                  )}
                  Aprobar todos ({pendingCount})
                </Button>
              </Can>
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-gray-100">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar persona o club..."
              className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 text-sm transition-all duration-300"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as RegistrationStatus | "all")
            }
            className="px-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 text-sm transition-all duration-300"
          >
            <option value="all">Activas (sin cancelados/rechazados)</option>
            <option value="pending">Pendientes</option>
            <option value="approved">Aprobados</option>
            <option value="rejected">Rechazados</option>
            <option value="cancelled">Cancelados</option>
          </select>

          {canManage && (
            <button
              onClick={() => setShowOnlyPending(!showOnlyPending)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                showOnlyPending
                  ? "bg-amber-100 text-amber-700 ring-2 ring-amber-300"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              <Filter className="w-3.5 h-3.5" />
              Solo pendientes
            </button>
          )}

          <div className="flex items-center gap-1 ml-auto text-xs">
            <button
              onClick={expandAll}
              className="px-2 py-1 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
            >
              Expandir
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={collapseAll}
              className="px-2 py-1 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
            >
              Colapsar
            </button>
          </div>
        </div>

        {/* Lista de clubes */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div>
          ) : clubGroups.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Users className="w-8 h-8 text-gray-500" />
              </div>
              <p className="font-medium">No hay inscripciones</p>
              <p className="text-sm mt-1">
                {searchTerm
                  ? "No se encontraron resultados"
                  : "Aún no hay inscripciones para este evento"}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {clubGroups.map(([clubId, group]) => {
                const isExpanded = expandedClubs.has(clubId);
                const clubPendingIds = group.registrations
                  .filter((r) => r.status === "pending")
                  .map((r) => r.id);
                const allClubSelected =
                  clubPendingIds.length > 0 &&
                  clubPendingIds.every((id) => selectedRegistrations.has(id));

                return (
                  <div key={clubId}>
                    {/* Club header — siempre visible */}
                    <div
                      className={`flex items-center gap-3 px-5 py-3 cursor-pointer hover:bg-gray-50 transition-colors duration-150 ${
                        group.pending > 0 ? "bg-amber-50/40" : ""
                      }`}
                    >
                      {/* Checkbox del club */}
                      {canManage && group.pending > 0 && (
                        <input
                          type="checkbox"
                          checked={allClubSelected}
                          onChange={() => toggleClubSelection(clubId)}
                          className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 shrink-0"
                          title={`Seleccionar ${group.pending} pendientes de ${group.clubName}`}
                        />
                      )}

                      {/* Toggle expand */}
                      <button
                        onClick={() => toggleClub(clubId)}
                        className="flex items-center gap-2 flex-1 min-w-0 text-left"
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                        )}
                        <span className="font-semibold text-gray-900 truncate">
                          {group.clubName}
                        </span>
                      </button>

                      {/* Stats del club */}
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant="default" className="text-xs">
                          {group.total}
                        </Badge>
                        {group.pending > 0 && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">
                            {group.pending} pend.
                          </Badge>
                        )}
                        {group.approved > 0 && (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            {group.approved} aprob.
                          </Badge>
                        )}
                        <span className="text-xs text-gray-500 w-24 text-right">
                          ${group.totalAmount.toLocaleString("es-CO")}
                        </span>

                        {/* Aprobar club completo */}
                        {canManage && group.pending > 0 && (
                          <Button
                            size="sm"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleApproveClub(clubId);
                            }}
                            disabled={isBulkProcessing}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs h-7 px-2"
                          >
                            {isBulkProcessing ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <>
                                <Check className="w-3 h-3 mr-1" />
                                Aprobar {group.pending}
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Miembros del club — expandible */}
                    {isExpanded && (
                      <div className="bg-white border-t border-gray-100">
                        {/* Encabezado de columnas */}
                        <div className="grid grid-cols-[auto_1fr_120px_90px_80px_80px] gap-2 px-5 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          {canManage && <span className="w-4" />}
                          <span>Persona</span>
                          <span>Categoría</span>
                          <span>Estado</span>
                          <span className="text-right">Monto</span>
                          <span className="text-center">Acciones</span>
                        </div>

                        <div className="divide-y divide-gray-50">
                          {group.registrations.map((reg) => (
                            <div
                              key={reg.id}
                              className={`grid grid-cols-[auto_1fr_120px_90px_80px_80px] gap-2 items-center px-5 py-2.5 text-sm hover:bg-gray-50/50 transition-colors duration-100 ${
                                reg.status === "pending" ? "bg-amber-50/30" : ""
                              }`}
                            >
                              {canManage && (
                                <input
                                  type="checkbox"
                                  checked={selectedRegistrations.has(reg.id)}
                                  onChange={() => toggleSelection(reg.id)}
                                  className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                  disabled={reg.status !== "pending"}
                                />
                              )}

                              {/* Persona */}
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900 truncate">
                                  {reg.personName}
                                </p>
                                <p className="text-xs text-gray-400">
                                  {toSafeDate(
                                    reg.registrationDate,
                                  ).toLocaleDateString("es-CO", {
                                    day: "numeric",
                                    month: "short",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </p>
                              </div>

                              {/* Categoría */}
                              <span className="text-gray-600 truncate">
                                {categoryLabels[reg.category] || reg.category}
                              </span>

                              {/* Estado */}
                              <Badge
                                className={`${statusColors[reg.status]} text-xs w-fit`}
                              >
                                {statusLabels[reg.status]}
                              </Badge>

                              {/* Monto */}
                              <span className="text-right font-medium text-gray-700">
                                ${reg.unitPrice.toLocaleString("es-CO")}
                              </span>

                              {/* Acciones */}
                              <div className="flex justify-center gap-1">
                                {canManage && reg.status === "pending" && (
                                  <>
                                    <button
                                      onClick={() => handleApprove(reg)}
                                      disabled={updateStatus.isPending}
                                      className="p-1.5 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                                      title="Aprobar"
                                    >
                                      <Check className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleReject(reg)}
                                      disabled={updateStatus.isPending}
                                      className="p-1.5 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                                      title="Rechazar"
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                                {reg.status !== "pending" && (
                                  <span className="text-gray-300">—</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
